import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { AutoCompleteSelectUI, CheckboxUI, FormErrorUI, FormLabelUI, InputUI } from '../Interface';
import { DocumentDirection } from '../../config/DocumentDirection';
import { DocumentType } from '../../config/DocumentType';
import { formatNavOnlineDate, getNavOnlineStatus } from '../../utils/navOnlineStatus';
import NavOnlineHistoryDialog from './NavOnlineHistoryDialog';
import documentService from '../../service/newDoc.service';
import { InvoiceModificationScope } from '../../config/document';

const DocumentAdditional = ({
  viewField,
  autocompleteOptions,
  selectedValues,
  autocompleteData,
  documentData,
  docTypeId,
  currentDocType,
  setRefetchTable,
  canManuallyAcceptNav = false,
}) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const [navHistoryStatus, setNavHistoryStatus] = useState(null);
  const customerTaxRequestIdRef = useRef(0);
  const savedDocument =
    Boolean(values.docId) && Number(values.docId) === Number(selectedValues?.docId) && selectedValues?.mode !== 'copy';
  const navRelevant = [
    DocumentType.ARInvoice,
    DocumentType.ARInvoiceModification,
    DocumentType.DownPayment,
  ].includes(Number(docTypeId));
  const isInvoiceModification = Number(docTypeId) === DocumentType.ARInvoiceModification;
  const allowsInvoiceHeaderModification =
    isInvoiceModification &&
    [InvoiceModificationScope.HEAD, InvoiceModificationScope.ALL].includes(values.modificationScope);
  const isInvoice = Boolean(currentDocType?.invoice ?? documentData?.invoice);
  const navOnline = navHistoryStatus?.source === selectedValues ? navHistoryStatus.current : selectedValues?.navOnline;
  const navStatus = getNavOnlineStatus(navOnline);
  const isOutgoing = documentData?.direction === DocumentDirection.OUTGOING;
  const sellerBankAccounts = useMemo(
    () =>
      (autocompleteData?.bankAccount ?? [])
        .map((bankAccount) => ({
          label: [bankAccount.name, bankAccount.bankName, bankAccount.bankAccountNumber, bankAccount.currencyCode]
            .filter(Boolean)
            .join(' - '),
          value: bankAccount.bankAccountNumber,
        })),
    [autocompleteData?.bankAccount]
  );
  const handleNavHistoryLoaded = useCallback(
    (history) => {
      setNavHistoryStatus({ source: selectedValues, current: history.current });
    },
    [selectedValues]
  );

  const handleFieldChange = async (field, newVal) => {
    const selected = newVal?.value ?? null;
    await setFieldValue(field, selected);
    await validateField(field);
  };

  const customerTaxCalculationContext = JSON.stringify({
    companyId: values.companyId ?? null,
    hunTaxNumber: values.hunTaxNumber ?? null,
    euTaxNumber: values.euTaxNumber ?? null,
    foreignTaxNumber: values.foreignTaxNumber ?? null,
    vatGroupNumber: values.vatGroupNumber ?? null,
    billingAdressId: values.billingAdressId ?? null,
    billingCountry: values.billingCountry ?? null,
    billingCountryCode: values.billingCountryCode ?? null,
    shippingAdressId: values.shippingAdressId ?? null,
    shippingCountry: values.shippingCountry ?? null,
    shippingCountryCode: values.shippingCountryCode ?? null,
    docLine: (values.docLine ?? []).map((line, index) => ({
      vatCode: line.vatCode ?? '',
      visOrder: line.visOrder ?? index,
      lineNum: line.lineNum ?? index,
    })),
  });

  useEffect(() => {
    if (viewField || !isInvoice || !docTypeId || isInvoiceModification) return undefined;

    const requestId = ++customerTaxRequestIdRef.current;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      const context = JSON.parse(customerTaxCalculationContext);
      documentService
        .calculateCustomerTaxFields(
          {
            docId: selectedValues?.mode === 'copy' ? null : values.docId || null,
            docTypeId: Number(docTypeId),
            ...context,
          },
          controller.signal
        )
        .then((data) => {
          if (requestId !== customerTaxRequestIdRef.current) return;

          [
            'hunTaxNumber',
            'euTaxNumber',
            'foreignTaxNumber',
            'vatGroupNumber',
            'customerTaxId',
            'customerTaxIdType',
            'groupMemberTaxNumber',
            'billingCountryCode',
            'shippingCountryCode',
          ].forEach((field) => setFieldValue(field, data?.[field] ?? null, false));
        })
        .catch(() => {});
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [
    customerTaxCalculationContext,
    docTypeId,
    isInvoiceModification,
    isInvoice,
    selectedValues?.mode,
    setFieldValue,
    values.docId,
    viewField,
  ]);

  return (
    <div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-5">
        {savedDocument && navRelevant && navOnline?.enabled && (
          <Box className="col-span-4 md:col-span-1">
            <FormLabelUI text="NAV státusz" />
            <Box mt={1}>
              <Tooltip title="NAV kommunikációs előzmények megnyitása">
                <Chip
                  label={navStatus.label}
                  color={navStatus.color}
                  size="small"
                  variant="outlined"
                  onClick={() => setNavDialogOpen(true)}
                  aria-label={`NAV előzmények: ${navStatus.label}`}
                />
              </Tooltip>
            </Box>
            {navOnline.nextRetryAt && (
              <Typography variant="caption" component="p">
                Újraküldés: {formatNavOnlineDate(navOnline.nextRetryAt)}
              </Typography>
            )}
          </Box>
        )}
        {documentData?.invoiceConfirmation && (
          <div className="col-span-4 md:col-span-1">
            <Field
              name="invoiceConfirmation"
              type="checkbox"
              as={CheckboxUI}
              fullWidth
              label={<FormLabelUI text="Számlaigazolás" />}
              disabled={viewField}
            />
          </div>
        )}
        {isOutgoing && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="sellerBankAccount"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={
                viewField ||
                (isInvoiceModification && !allowsInvoiceHeaderModification) ||
                (Number(docTypeId) !== DocumentType.ARInvoiceManual &&
                  selectedValues?.mode !== 'copy' &&
                  Boolean(selectedValues?.finalizedAt))
              }
              label={<FormLabelUI text="Saját bankszámla" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.sellerBankAccount} />}
              InputLabelProps={{ shrink: Boolean(values.sellerBankAccount) }}
              onChange={(_e, newVal) => {
                const selectedBankAccount = (autocompleteData?.bankAccount ?? []).find(
                  (bankAccount) => bankAccount.bankAccountNumber === newVal?.value
                );
                setFieldValue('sellerBankAccount', selectedBankAccount?.bankAccountNumber ?? null);
                setFieldValue('sellerBankName', selectedBankAccount?.bankName ?? null);
              }}
              selectedValue={values.sellerBankAccount}
              selectedLabelValue={sellerBankAccounts.find((item) => item.value === values.sellerBankAccount)?.label}
              dataset={sellerBankAccounts}
            />
          </div>
        )}
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="priceListId"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={viewField || isInvoiceModification}
            label={<FormLabelUI text="Árlista" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.priceListId} />}
            InputLabelProps={{ shrink: Boolean(values.priceListId) }}
            onChange={(_e, newVal) => handleFieldChange('priceListId', newVal)}
            selectedValue={values.priceListId}
            selectedLabelValue={autocompleteData?.priceList?.find((p) => p.id === values.priceListId)?.name}
            dataset={autocompleteOptions?.priceList}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="salesOrPurchasePersonId"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={viewField}
            label={
              <FormLabelUI text={documentData.direction === DocumentDirection.INCOMING ? 'Beszerző' : 'Értékesítő'} />
            }
            variant="standard"
            helperText={<FormErrorUI message={errors.salesOrPurchasePersonId} />}
            InputLabelProps={{ shrink: Boolean(values.salesOrPurchasePersonId) }}
            onChange={(_e, newVal) => handleFieldChange('salesOrPurchasePersonId', newVal)}
            selectedValue={values.salesOrPurchasePersonId}
            selectedLabelValue={
              autocompleteData?.salesOrPurchasePerson?.find((p) => p.id === values.salesOrPurchasePersonId)?.name
            }
            dataset={autocompleteOptions?.salesOrPurchasePerson}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="langCode"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={viewField || isInvoiceModification}
            label={<FormLabelUI text="Nyelv" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.langCode} />}
            InputLabelProps={{ shrink: Boolean(values.langCode) }}
            onChange={(_e, newVal) => handleFieldChange('langCode', newVal)}
            selectedValue={values.langCode}
            selectedLabelValue={autocompleteData?.language?.find((p) => p.code === values.langCode)?.name}
            dataset={autocompleteOptions?.language}
          />
        </div>
      </div>
      {isInvoice && (
        <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-5">
          <div className="col-span-4 md:col-span-1">
            <Field
              type="text"
              name="customerTaxId"
              as={InputUI}
              fullWidth
              disabled={true}
              label={<FormLabelUI text="Számlán használt adószám" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.customerTaxId} />}
              InputLabelProps={{ shrink: Boolean(values.customerTaxId) }}
            />
          </div>

          <div className="col-span-4 md:col-span-1">
            <Field
              type="text"
              name="customerTaxIdType"
              as={InputUI}
              fullWidth
              disabled={true}
              label={<FormLabelUI text="Adószám típusa" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.customerTaxIdType} />}
              InputLabelProps={{ shrink: Boolean(values.customerTaxIdType) }}
            />
          </div>

          <div className="col-span-4 md:col-span-1">
            <Field
              type="text"
              name="groupMemberTaxNumber"
              as={InputUI}
              fullWidth
              disabled={true}
              label={<FormLabelUI text="Csoporttag adószáma" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.groupMemberTaxNumber} />}
              InputLabelProps={{ shrink: Boolean(values.groupMemberTaxNumber) }}
            />
          </div>
        </div>
      )}
      {isInvoiceModification && (
        <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-5">
          <div className="col-span-4 md:col-span-1">
            <Field
              type="number"
              name="originalInvoiceId"
              as={InputUI}
              fullWidth
              disabled={true}
              label={<FormLabelUI text="Eredeti számla azonosítója" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.originalInvoiceId} />}
              InputLabelProps={{ shrink: Boolean(values.originalInvoiceId) }}
            />
          </div>
          <div className="col-span-4 md:col-span-1">
            <Field
              type="text"
              name="originalInvoiceNumber"
              as={InputUI}
              fullWidth
              disabled={true}
              label={<FormLabelUI text="Eredeti számla száma" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.originalInvoiceNumber} />}
              InputLabelProps={{ shrink: Boolean(values.originalInvoiceNumber) }}
            />
          </div>
          <div className="col-span-4 md:col-span-1">
            <Field
              type="number"
              name="modificationIndex"
              as={InputUI}
              value={values.modificationIndex ?? values.modificationIndexPreview ?? ''}
              fullWidth
              disabled={true}
              label={<FormLabelUI text="Módosítás sorszáma" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.modificationIndex} />}
              InputLabelProps={{ shrink: Boolean(values.modificationIndex ?? values.modificationIndexPreview) }}
            />
          </div>
          <div className="col-span-4 md:col-span-1">
            <Field
              type="text"
              name="modificationReason"
              as={InputUI}
              fullWidth
              disabled={viewField}
              label={<FormLabelUI text="Módosítás oka" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.modificationReason} />}
              InputLabelProps={{ shrink: Boolean(values.modificationReason) }}
            />
          </div>
        </div>
      )}
      <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-5">
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="hunTaxNumber"
            as={InputUI}
            fullWidth
            disabled={true}
            label={<FormLabelUI text="Magyar adószám" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.hunTaxNumber} />}
            InputLabelProps={{ shrink: Boolean(values.hunTaxNumber) }}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="euTaxNumber"
            as={InputUI}
            fullWidth
            disabled={true}
            label={<FormLabelUI text="Eu adószám" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.euTaxNumber} />}
            InputLabelProps={{ shrink: Boolean(values.euTaxNumber) }}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="foreignTaxNumber"
            as={InputUI}
            fullWidth
            disabled={true}
            label={<FormLabelUI text="Külföldi adószám" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.foreignTaxNumber} />}
            InputLabelProps={{ shrink: Boolean(values.foreignTaxNumber) }}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="vatGroupNumber"
            as={InputUI}
            fullWidth
            disabled={true}
            label={<FormLabelUI text="Csoportos adószám" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.vatGroupNumber} />}
            InputLabelProps={{ shrink: Boolean(values.vatGroupNumber) }}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="billingCountryCode"
            as={InputUI}
            fullWidth
            disabled={true}
            label={<FormLabelUI text="Számlázási országkód" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.billingCountryCode} />}
            InputLabelProps={{ shrink: Boolean(values.billingCountryCode) }}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="shippingCountryCode"
            as={InputUI}
            fullWidth
            disabled={true}
            label={<FormLabelUI text="Szállítási országkód" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.shippingCountryCode} />}
            InputLabelProps={{ shrink: Boolean(values.shippingCountryCode) }}
          />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-5">
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="createdBy"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={true}
            label={<FormLabelUI text="Létrehozta" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.createdBy} />}
            InputLabelProps={{ shrink: Boolean(values.createdBy) }}
            onChange={(_e, newVal) => handleFieldChange('createdBy', newVal)}
            selectedValue={values.createdBy}
            selectedLabelValue={autocompleteData?.users?.find((p) => p.userId === values.createdBy)?.userName}
            dataset={autocompleteOptions?.users}
            isOptionEqualToValue={(option, value) => option.value === values.createdBy}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="finalizedBy"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={true}
            label={<FormLabelUI text="Véglegesítette" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.finalizedBy} />}
            InputLabelProps={{ shrink: Boolean(values.finalizedBy) }}
            selectedValue={values.finalizedBy}
            selectedLabelValue={autocompleteData?.users?.find((p) => p.userId === values.finalizedBy)?.userName}
            dataset={autocompleteOptions?.users}
            isOptionEqualToValue={(option, value) => option.value === values.finalizedBy}
          />
        </div>
      </div>
      <NavOnlineHistoryDialog
        canManuallyAccept={canManuallyAcceptNav}
        open={navDialogOpen && savedDocument && navRelevant}
        onClose={() => {
          setNavDialogOpen(false);
          setRefetchTable?.(true);
        }}
        documentTypeId={Number(docTypeId)}
        documentId={values.docId}
        documentNumber={values.docNum}
        onLoaded={handleNavHistoryLoaded}
      />
    </div>
  );
};

export default DocumentAdditional;
